import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { tr } from '../lang';
import { reportOops } from '../game/oops';

// What a page shows when it breaks. React Router hands a page's error to its route's errorElement; before this, that
// was the ErrorBoundary class with no children, which drew nothing at all, so a broken page went blank white
// (Nov 2 2026: the projector in a Lantern Line game).
//
// A live game (a student's laptop, the projector, the control laptop) loads itself again after a moment: the room,
// the seat and the scores are all on the server, so a reload walks straight back in. A page that keeps breaking stops
// trying after a few goes and offers the button instead. Anywhere else the page says so, with a button that reloads
// the same page (not the home page).

const LIVE = /^\/play\/(live|host)\b/;
const KEY = 'gk-reloads';
const WINDOW_MS = 120_000;
const MAX = 3;

function reloadsLately(): number[] {
  try {
    const now = Date.now();
    return (JSON.parse(sessionStorage.getItem(KEY) || '[]') as number[]).filter((t) => now - t < WINDOW_MS);
  } catch {
    return [];
  }
}

export function RouteError() {
  const error = useRouteError();
  const live = LIVE.test(location.pathname);
  const [auto] = useState(() => live && reloadsLately().length < MAX);
  useEffect(() => {
    console.error('Page error:', error);
    reportOops(`The page ${location.pathname}`, error);
  }, [error]);
  useEffect(() => {
    if (!auto) return;
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(KEY, JSON.stringify([...reloadsLately(), Date.now()]));
      } catch {
        /* a private window: it reloads all the same */
      }
      location.reload();
    }, 1500);
    return () => clearTimeout(t);
  }, [auto]);

  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 16,
        background: '#1f1a24',
        color: '#f3ead8',
        textAlign: 'center',
        fontSize: 18,
      }}
    >
      <p style={{ margin: 0 }}>
        {auto
          ? tr('One moment, the game is coming back… · ちょっと待ってね、ゲームに戻ります…')
          : tr('Something went wrong on this page. · このページで問題が起きました。')}
      </p>
      {!auto && (
        <button
          type="button"
          onClick={() => location.reload()}
          style={{ font: 'inherit', padding: '8px 18px', borderRadius: 8, border: 0, background: '#f2a13b', color: '#1f1a24', cursor: 'pointer' }}
        >
          {tr('Reload · 再読み込み')}
        </button>
      )}
    </div>
  );
}
